import z from 'zod';

export const SignInStateSchema = z.enum(['SignedOut', 'SignedIn']);
export type SignInState = z.infer<typeof SignInStateSchema>;

// Mirroring the native object companion format
// @wc-ignore
export const SignInState = {
	SignedOut: 'SignedOut',
	SignedIn: 'SignedIn'
} as const;

export const UserSchema = z.object({
	displayName: z.string().nullable(),
	email: z.string().nullable(),
	uid: z.string(),
	// Matches a method that takes 0 arguments and returns a Promise<string>
	getIdToken: z.function({ output: z.promise(z.string()) })
});
export type User = z.infer<typeof UserSchema>;

export const AuthResultSchemaFirebase = z.object({
	user: UserSchema,
	provider: z.enum(['google']),
	method: z.literal('firebase'),
	idToken: z.string()
});
export const AuthResultSchemaJwt = z.object({
	user: UserSchema,
	provider: z.enum(['api']),
	method: z.literal('jwt'),
	idToken: z.string(),
	refreshToken: z.string()
});
export const AuthResultSchemaBetterAuthFirebase = z.object({
	user: UserSchema,
	provider: z.literal('firebase'),
	method: z.literal('better-auth'),
	idToken: z.string(),
	sessionToken: z.string()
});
export const AuthResultSchemaBetterAuthEmailOTP = z.object({
	user: UserSchema,
	provider: z.literal('email-otp'),
	method: z.literal('better-auth'),
	sessionToken: z.string()
});
export const AuthResultSchemaBetterAuth = z.discriminatedUnion('provider', [
	AuthResultSchemaBetterAuthFirebase,
	AuthResultSchemaBetterAuthEmailOTP
]);
export const AuthResultSchema = z.discriminatedUnion('method', [
	AuthResultSchemaJwt,
	AuthResultSchemaFirebase,
	AuthResultSchemaBetterAuth
]);
export type AuthResultFirebase = z.infer<typeof AuthResultSchemaFirebase>;
export type AuthResultJwt = z.infer<typeof AuthResultSchemaJwt>;
export type AuthResultBetterAuth = z.infer<typeof AuthResultSchemaBetterAuth>;
export type AuthResult = z.infer<typeof AuthResultSchema>;

export type AuthService = {
	getSignInState: () => SignInState;
	signOut: () => Promise<void>;
	authReady: () => Promise<void>;
	onAuthStateChanged: (callback: (user: User | null) => void) => () => void;
	getUser: () => User | null;
	firebaseAuthReady: () => boolean;
	signInWithGoogleIdToken: (idToken: string) => Promise<void>;
	signInWithGoogle: () => Promise<AuthResult>;
	requestEmailOtp: (email: string) => Promise<void>;
	signInWithEmailOtp: (email: string, otp: string) => Promise<AuthResult>;
	signInWithJwtTokens: (idToken: string, refreshToken: string) => Promise<AuthResult>;
	upgradeToJwt: () => Promise<AuthResultJwt>;
};

export function createServerRenderingAuthService(): AuthService {
	return {
		getSignInState: () => SignInState.SignedOut,
		async signOut() {
			throw Error('not available during server rendering');
		},
		async authReady() {
			throw Error('not available during server rendering');
		},
		onAuthStateChanged() {
			throw Error('not available during server rendering');
		},
		getUser() {
			throw Error('not available during server rendering');
		},
		firebaseAuthReady() {
			return false;
		},
		async signInWithGoogleIdToken() {
			throw Error('not available during server rendering');
		},
		async signInWithGoogle() {
			throw Error('not available during server rendering');
		},
		async requestEmailOtp() {
			throw Error('not available during server rendering');
		},
		async signInWithEmailOtp() {
			throw Error('not available during server rendering');
		},
		async signInWithJwtTokens() {
			throw Error('not available during server rendering');
		},
		async upgradeToJwt() {
			throw Error('not available during server rendering');
		}
	};
}
